import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { ModalShell } from '../ui/ModalShell';
import { RegistrationCodePanel } from './RegistrationCodePanel';
import { ScoutIcon } from './ScoutIcon';

// Associates the footer submit button with the create-mode form that lives in the
// shell body (both render inside the same portaled panel), so Generate code can
// submit without a second form or a duplicate handler.
const CREATE_FORM_ID = 'add-device-form';

export function AddDeviceModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'create' | 'code'>('create');
  const [deviceName, setDeviceName] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  const [registrationCodeExpires, setRegistrationCodeExpires] = useState('');

  const createDeviceMutation = useMutation({
    mutationFn: async () => {
      const name = deviceName || 'My Scout';
      return api.createDevice(name, 'scout');
    },
    onSuccess: (data) => {
      setRegistrationCode(data.registration_code);
      setRegistrationCodeExpires(data.registration_code_expires_at);
      setMode('code');
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const handleCreateDevice = (e: React.FormEvent) => {
    e.preventDefault();
    createDeviceMutation.mutate();
  };

  const footer =
    mode === 'create' ? (
      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          form={CREATE_FORM_ID}
          disabled={createDeviceMutation.isPending}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          {createDeviceMutation.isPending ? 'Creating...' : 'Generate code'}
        </button>
      </div>
    ) : (
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
        >
          Done
        </button>
      </div>
    );

  return (
    <ModalShell
      open
      onClose={onClose}
      tone="dark"
      size="sm"
      keyboardAlign="above"
      title={mode === 'code' ? 'Your registration code' : 'Register a device'}
      footer={footer}
    >
      {mode === 'create' && (
        <form id={CREATE_FORM_ID} onSubmit={handleCreateDevice} className="space-y-5">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-slate-700/60 bg-slate-900/60 text-cyan-400">
              <ScoutIcon className="h-10 w-10" />
            </div>
            <p className="text-sm text-slate-300">
              Name your Scout and we'll make a registration code for it. You then enter the code on the Scout.
            </p>
          </div>

          <div>
            <label htmlFor="add-device-name" className="block text-sm font-medium text-slate-300 mb-1.5">
              Device name (optional)
            </label>
            <input
              id="add-device-name"
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="My Scout"
              className="w-full px-3.5 py-2.5 bg-slate-900/60 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </div>

          {createDeviceMutation.isError && (
            <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {(createDeviceMutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error
                ?? "We couldn't make a code. Please try again."}
            </p>
          )}
        </form>
      )}

      {mode === 'code' && (
        <RegistrationCodePanel code={registrationCode} expiresAt={registrationCodeExpires || null} steps="register" />
      )}
    </ModalShell>
  );
}
